import './configure-zod'

import '@jobilla/starfield/styles.css'
import { AppVersionCheck } from '@/features/app-version/app-version-check'
import { OpenFeature, OpenFeatureProvider } from '@openfeature/react-sdk'
import * as Sentry from '@sentry/react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { NotFoundComponent, RouteErrorComponent } from './components'
import './globals.css'
import { useLanguagePreference } from './hooks'
import './i18n'
import {
	isAuthEndpoint,
	isValidationFailure,
	NexusAuthInterceptorProvider,
	NexusAuthProvider,
	NexusOpenFeatureProvider,
	redactSensitiveFields,
	useAuth,
} from './lib'
import { PostHogProviderWrapper } from './PostHogProviderWrapper'
import { routeTree } from './routeTree.gen.ts'
import { JobiLoader, logger, ToastProvider } from './ui'

import '@fontsource-variable/crimson-pro'
import '@fontsource-variable/dm-sans'
import '@fontsource-variable/jetbrains-mono'
import '@fontsource-variable/lora'
import '@fontsource-variable/merriweather'
import '@fontsource-variable/noto-sans'
import '@fontsource-variable/open-sans'
import '@fontsource-variable/playfair-display'
import '@fontsource-variable/raleway'
import '@fontsource-variable/rubik'

function logAuthError(error: Error, source: 'query' | 'mutation', key: unknown) {
	if (!(error instanceof AxiosError)) {
		return
	}
	const status = error.response?.status
	if (status !== 401 && status !== 403) {
		return
	}

	logger.warn(`Unauthorized API response (${status})`, {
		category: 'api',
		data: {
			status,
			method: error.config?.method?.toUpperCase(),
			url: error.config?.url,
			source,
			key: JSON.stringify(key),
		},
	})
}

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error, query) => logAuthError(error, 'query', query.queryKey),
	}),
	mutationCache: new MutationCache({
		onError: (error, _variables, _context, mutation) => {
			logAuthError(error, 'mutation', mutation.options.mutationKey)

			// No subscription: refetched entitlements send the user to the paywall.
			if (error instanceof AxiosError && error.response?.status === 402) {
				void queryClient.invalidateQueries({ queryKey: [{ url: '/:organisation/entitlements' }] })
			}
		},
	}),
	defaultOptions: {
		// Under Playwright E2E, avoid React Query's default 3-retry + exponential
		// backoff — failing fast keeps error-state assertions inside the 5s
		// default toBeVisible timeout.
		queries: import.meta.env.VITE_E2E === 'true' ? { retry: false } : {},
	},
})
const router = createRouter({
	routeTree,
	defaultPendingComponent: () => (
		<div className="flex items-center justify-center h-screen relative">
			<JobiLoader />
		</div>
	),
	defaultErrorComponent: RouteErrorComponent,
	defaultNotFoundComponent: NotFoundComponent,
	context: {
		// Overwritten by RouterProvider's context below; App gates that render on
		// auth having loaded, so this stands in only for the pre-mount router.
		auth: { isSignedIn: false, user: null, role: null, permissions: [] },
		queryClient,
	},
	defaultPreload: 'intent',
	// Since we're using React Query, we don't want loader calls to ever be stale
	// This will ensure that the loader is always called when the route is preloaded or visited
	defaultPreloadStaleTime: 0,
	scrollRestoration: true,
})

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

function App() {
	const auth = useAuth()
	useLanguagePreference()
	const isLoaded = !auth.isLoading
	const isSignedIn = !!auth.user
	const userId = auth.user?.id

	const authContext = useMemo(() => ({
		isSignedIn,
		user: auth.user,
		role: auth.role,
		permissions: auth.permissions,
		// Only recalculate when auth state meaningfully changes,
		// not on every object reference change from WorkOS
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [isSignedIn, userId, auth.role, auth.permissions])

	const initialAuthRef = useRef({ isSignedIn, userId })
	useEffect(() => {
		// Only invalidate on actual auth state changes (sign-in/out), not on initial mount.
		// The router already gets the correct context on first render.
		// IMPORTANT: Don't invalidate while still loading — RouterProvider hasn't rendered yet,
		// so the router would use its stale initial context (auth: undefined).
		if (!isLoaded) { return }
		if (
			isSignedIn !== initialAuthRef.current.isSignedIn
			|| userId !== initialAuthRef.current.userId
		) {
			initialAuthRef.current = { isSignedIn, userId }
			void router.invalidate()
		}
	}, [isLoaded, isSignedIn, userId])

	// Don't render the router until auth is ready.
	// This prevents the loading → loaded transition from triggering
	// router.invalidate(), which would cancel in-flight queries.
	if (!isLoaded) {
		return (
			<div className="flex items-center justify-center h-screen relative">
				<JobiLoader />
			</div>
		)
	}

	return (
		<ToastProvider>
			<AppVersionCheck router={router} />
			<RouterProvider router={router} defaultPreload="intent" context={{ auth: authContext }} />
		</ToastProvider>
	)
}

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	enabled: import.meta.env.VITE_ENVIRONMENT !== 'development'
		&& import.meta.env.VITE_E2E !== 'true',
	environment: import.meta.env.VITE_APP_ENV ?? import.meta.env.VITE_ENVIRONMENT,
	replaysOnErrorSampleRate: 1,
	tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === 'production' ? 1 : 0,
	sendDefaultPii: false,
	ignoreErrors: [
		'Password has been found in an online data breach.'
		+ ' For account safety, please use a different password.',
		'ResizeObserver loop completed with undelivered notifications.',
		'Request aborted',
		/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
		/Error invoking \w+: Java object is gone/,
		/Error invoking \w+: Java bridge method invocation error/,
		/Invalid call to runtime\.sendMessage\(\)/,
	],
	_experiments: {
		enableLogs: true,
	},
	integrations: [
		Sentry.thirdPartyErrorFilterIntegration({
			// Must match applicationKey in vite.config.mts
			filterKeys: ['kone-tas'],
			behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
		}),
		Sentry.tanstackRouterBrowserTracingIntegration(router),
		Sentry.consoleLoggingIntegration({ levels: ['error', 'warn'] }),
		Sentry.replayIntegration({
			maskAllText: false,
		}),
	],
	beforeSend(event, hint) {
		const error = hint.originalException

		// Browser-initiated aborts (page unload, user stop) are not failures.
		// Axios's fetch adapter copies the DOMException name onto its AxiosError.
		if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
			return null
		}

		// Field validation errors are user input mistakes that the screen shows, not app errors.
		if (isValidationFailure(error)) {
			return null
		}

		// Extract the API endpoint URL from Axios errors instead of using the page URL
		if (error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError) {
			const axiosError = error as AxiosError

			// Get the request URL from the Axios error
			const requestUrl = axiosError.config?.url
			const baseURL = axiosError.config?.baseURL
			const method = axiosError.config?.method?.toUpperCase()
			const status = axiosError.response?.status

			// Construct full API endpoint URL
			if (requestUrl) {
				const fullUrl = baseURL && !requestUrl.startsWith('http')
					? `${baseURL.replace(/\/$/, '')}/${requestUrl.replace(/^\//, '')}`
					: requestUrl

				// Strip query string, then UUIDs and numeric IDs to group similar endpoints
				const pathname = fullUrl.split(/[?#]/)[0]
				const normalizedUrl = pathname.replace(
					/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
					'{id}',
				).replace(/\/\d+(?=\/|$)/g, '/{id}')

				// Group Sentry issues by method + endpoint pattern + status code
				event.fingerprint = [
					method ?? 'UNKNOWN',
					normalizedUrl,
					String(status ?? 'no-response'),
				]

				// Set the transaction name to the API endpoint
				if (event.transaction) {
					event.transaction = `${method} ${fullUrl}`
				}

				// Also add as a tag for easy filtering
				event.tags = {
					...event.tags,
					api_endpoint: fullUrl,
					http_method: method,
					http_status: status?.toString(),
				}

				// Update the request context
				event.request = {
					...event.request,
					url: fullUrl,
					method,
				}
			}

			if (axiosError.response?.data) {
				event.contexts = {
					...event.contexts,
					response_body: typeof axiosError.response.data === 'object'
						? redactSensitiveFields(axiosError.response.data) as Record<string, unknown>
						: { data: String(axiosError.response.data).slice(0, 8192) },
				}
			}

			// Auth request bodies are credentials (passwords, one-time codes, pending tokens).
			if (axiosError.config?.data && !isAuthEndpoint(requestUrl ?? '')) {
				try {
					const payload = typeof axiosError.config.data === 'string'
						? JSON.parse(axiosError.config.data) as Record<string, unknown>
						: axiosError.config.data as Record<string, unknown>

					event.contexts = {
						...event.contexts,
						request_payload: redactSensitiveFields(payload) as Record<string, unknown>,
					}
				} catch {
					event.contexts = {
						...event.contexts,
						request_payload: { raw: String(axiosError.config.data).slice(0, 8192) },
					}
				}
			}
		}

		// thirdPartyErrorFilterIntegration only classifies events that have frames.
		const exceptionValues = event.exception?.values

		if (exceptionValues?.length && exceptionValues.every(value => !value.stacktrace?.frames?.length)) {
			event.tags = { ...event.tags, no_stack_frames: true }
		}

		return event
	},
})

OpenFeature.setProvider(new NexusOpenFeatureProvider())

// Suppress benign ResizeObserver loop errors (caused by animation-driven layout changes).
// See https://github.com/WICG/resize-observer/issues/38
window.addEventListener('error', (e) => {
	if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
		e.stopImmediatePropagation()
	}
})

const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
	const root = createRoot(rootElement)
	root.render(
		<QueryClientProvider client={queryClient}>
			<NexusAuthProvider>
				<NexusAuthInterceptorProvider>
					<OpenFeatureProvider>
						<PostHogProviderWrapper>
							<App />
						</PostHogProviderWrapper>
					</OpenFeatureProvider>
				</NexusAuthInterceptorProvider>
			</NexusAuthProvider>
		</QueryClientProvider>,
	)
}
